import { Dispatch, SetStateAction, useState } from 'react'
import type { DragEvent } from 'react'
import { mdiCloudUploadOutline } from '@mdi/js'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { generateThenUploadTemplateZip } from './utils/generateThenUploadTemplateZip'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { currentUserSelector } from 'features/auth/login/selectors'
import { SpinnerCentered } from 'components/Loader'
import {
  DropzoneModalBody,
  DropzoneModalDesc,
  DropzoneModalIcon,
  DropzoneModalIconWrapper,
} from 'components/Modal/DropzoneModal/styles'
import { userAnyTopDomainIdSelector } from 'features/auth/permissions/selectors'
import { fetchTemplatesListThunk } from 'features/templates/slice'
import { sleep } from 'app/utils/sleep'
import { templatesPageMessages } from 'features/templates/messages'

interface TemplateDropZoneProps {
  handleClose: () => void
  setUploadedFilename: Dispatch<SetStateAction<string>>
  onSuccess: () => void
}

function TemplateDropZone({
  handleClose,
  setUploadedFilename,
  onSuccess,
}: TemplateDropZoneProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isDragActive, setIsDragActive] = useState(false)
  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const userAnyTopDomainId = useAppSelector(userAnyTopDomainIdSelector)
  const user = useAppSelector(currentUserSelector)
  const userId = user?.id

  const handleFilesDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setErrorMessage('')
    setIsLoading(true)
    setIsDragActive(false)
    try {
      // TODO: webkitGetAsEntry is an experimental technology, must be removed when new Savant upload service will be implemented
      const droppedFile = event?.dataTransfer?.items[0].webkitGetAsEntry()
      setUploadedFilename(droppedFile.name.trim())
      await generateThenUploadTemplateZip(
        droppedFile,
        rootDomainId,
        userId,
        formatMessage
      )
      // backend issue - it needs ~3 sec to update templates
      await sleep(3000)
      await dispatch(fetchTemplatesListThunk(userAnyTopDomainId))
      setIsLoading(false)
      onSuccess()
      handleClose()
    } catch (err) {
      setIsLoading(false)
      console.log('File upload failed', err)
      setErrorMessage(err.message)
    }
  }

  return (
    <DropzoneModalBody
      isDragActive={isDragActive}
      isDragReject={!!errorMessage}
      onDragOver={(event) => {
        event.preventDefault()
        setIsDragActive(true)
      }}
      onDragLeave={(event) => {
        event.preventDefault()
        setIsDragActive(false)
      }}
      onDrop={(event) => handleFilesDrop(event)}
    >
      <DropzoneModalIconWrapper>
        <DropzoneModalIcon path={mdiCloudUploadOutline} />
      </DropzoneModalIconWrapper>
      <DropzoneModalDesc isDragReject={!!errorMessage}>
        {!!errorMessage
          ? errorMessage
          : formatMessage(templatesPageMessages.uploadTemplateModalDesc)}
      </DropzoneModalDesc>
      {isLoading && <SpinnerCentered size="40" />}
    </DropzoneModalBody>
  )
}

export default TemplateDropZone
